import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookOpen, Save } from "lucide-react";

interface StoryNicknameDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (nickname: string) => void;
  initialNickname?: string;
}

export function StoryNicknameDialog({ isOpen, onClose, onSave, initialNickname = "" }: StoryNicknameDialogProps) {
  const [nickname, setNickname] = useState(initialNickname);

  // Update nickname when dialog opens with different initial value
  useEffect(() => {
    setNickname(initialNickname);
  }, [initialNickname, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(nickname.trim());
    onClose();
  };

  const handleCancel = () => {
    setNickname(initialNickname); // Reset to initial value
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Edit Story Nickname
          </DialogTitle>
          <DialogDescription>
            Give your story a nickname to make it easier to find and remember.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="story-nickname">Story Nickname</Label>
            <Input
              id="story-nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Enter a nickname for this story..."
              className="w-full"
              autoFocus
              data-testid="input-story-nickname"
            />
          </div>
          
          {nickname.trim() && (
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-700 font-medium">
                ✨ This story will be called: <strong>"{nickname.trim()}"</strong>
              </p>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              data-testid="button-cancel-nickname"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!nickname.trim()}
              data-testid="button-save-nickname"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Nickname
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}